'use client';
import React, { useState, useRef, useEffect, useCallback } from 'react';
import AppNav from '@/components/AppNav';
import { supabase } from '@/lib/supabase/client';

// ============================================================
// TYPES
// ============================================================

interface SourceRef {
  type: 'official' | 'secondary' | 'ai_analysis' | 'professional_advice';
  title: string;
  reference?: string;
  date?: string;
}

interface StructuredResponse {
  answer?: string;
  whyItMatters?: string;
  whatYouNeed?: string[];
  whatToDoNext?: string[];
  deadline?: string;
  documents?: string[];
  source?: SourceRef;
  humanReview?: string;
  actions?: Array<{ label: string; href: string }>;
  configurationError?: boolean;
}

interface Message {
  role: 'ai' | 'user';
  content: string;
  timestamp: Date;
  structured?: StructuredResponse;
  isStreaming?: boolean;
}

interface BusinessPassport {
  businessName: string;
  entityType: string;
  founders: number;
  incorporationDate?: string;
  trademarkStatus?: string;
  documentsVerified?: string;
  complianceHealth?: number;
  pendingItems?: string[];
  registrations?: Record<string, string>;
  gstStatus?: string;
  msmeStatus?: string;
  dpiitStatus?: string;
  directors?: string[];
  address?: string;
  businessActivity?: string;
}

interface ProactiveAlert {
  type: 'warning' | 'info' | 'danger' | 'success';
  title: string;
  description?: string;
  action: { label: string; href: string };
  priority?: 'high' | 'medium' | 'low';
}

// ============================================================
// CONSTANTS
// ============================================================

const SOURCE_LABELS: Record<string, { label: string; color: string; bg: string }> = {
  official: { label: 'Official Source', color: '#059669', bg: 'rgba(5,150,105,0.08)' },
  secondary: { label: 'Secondary Source', color: '#0284c7', bg: 'rgba(2,132,199,0.08)' },
  ai_analysis: { label: 'Lexigen AI Analysis', color: '#7c3aed', bg: 'rgba(124,58,237,0.08)' },
  professional_advice: { label: 'Professional Advice', color: '#b45309', bg: 'rgba(180,83,9,0.08)' },
};

const SUGGESTED_QUESTIONS = [
  'What registrations do I need?',
  'What filings are due this month?',
  'What documents are missing?',
  'What is my company structure?',
  'Can I apply for GST?',
  'What trademark classes are relevant?',
  'My director changed. What do I need to update?',
  'What compliance is due soon?',
];

const DEFAULT_PASSPORT: BusinessPassport = {
  businessName: 'Your Business',
  entityType: 'Private Limited Company',
  founders: 2,
  trademarkStatus: 'Not started',
  documentsVerified: '0/0',
  complianceHealth: 0,
  pendingItems: [],
  registrations: {},
};

// ============================================================
// HELPERS
// ============================================================

function formatMarkdown(text: string): string {
  return text
    .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.*?)\*/g, '<em>$1</em>')
    .replace(/^### (.*$)/gm, '<h3 class="font-bold text-ink text-[13px] mt-3 mb-1">$1</h3>')
    .replace(/^## (.*$)/gm, '<h2 class="font-bold text-ink text-[14px] mt-3 mb-1">$1</h2>')
    .replace(/^• (.*$)/gm, '<li class="ml-3 list-disc">$1</li>')
    .replace(/^\d+\. (.*$)/gm, '<li class="ml-3 list-decimal">$1</li>')
    .replace(/\n/g, '<br/>');
}

// ============================================================
// SUB-COMPONENTS
// ============================================================

function SourceBadge({ source }: { source: SourceRef }) {
  const cfg = SOURCE_LABELS[source.type] || SOURCE_LABELS.ai_analysis;
  return (
    <div
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold mt-2"
      style={{ background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.color}22` }}
    >
      <span aria-hidden="true">📎</span>
      <span>{cfg.label}</span>
      {source.title && <span className="opacity-70">· {source.title}</span>}
    </div>
  );
}

function StructuredResponseCard({ structured }: { structured: StructuredResponse }) {
  if (structured.configurationError) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 mt-2" role="alert">
        <div className="flex items-center gap-2 mb-2">
          <span className="text-amber-600 text-lg" aria-hidden="true">⚙️</span>
          <span className="font-bold text-amber-800 text-[13px]">AI Not Configured</span>
        </div>
        <p className="text-[12px] text-amber-700">
          Set <code className="bg-amber-100 px-1 rounded">OPENROUTER_API_KEY</code> in your environment variables to activate Lexigen AI.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-3 mt-1">
      {structured.answer && (
        <div>
          <p className="text-[11px] font-bold text-ink-muted uppercase tracking-wider mb-1">Answer</p>
          <div className="text-[13px] text-ink leading-relaxed" dangerouslySetInnerHTML={{ __html: formatMarkdown(structured.answer) }} />
        </div>
      )}
      {structured.whyItMatters && (
        <div className="rounded-lg p-3" style={{ background: 'rgba(99,102,241,0.04)', border: '1px solid rgba(99,102,241,0.1)' }}>
          <p className="text-[10px] font-bold text-indigo-600 uppercase tracking-wider mb-1">Why It Matters</p>
          <p className="text-[12px] text-ink-muted">{structured.whyItMatters}</p>
        </div>
      )}
      {structured.whatToDoNext && structured.whatToDoNext.length > 0 && (
        <div>
          <p className="text-[10px] font-bold text-ink-muted uppercase tracking-wider mb-1.5">What To Do Next</p>
          <ol className="space-y-1">
            {structured.whatToDoNext.map((step, i) => (
              <li key={i} className="flex items-start gap-2 text-[12px] text-ink-muted">
                <span className="w-4 h-4 rounded-full bg-indigo-100 text-indigo-600 text-[10px] font-bold flex items-center justify-center flex-shrink-0 mt-0.5" aria-hidden="true">{i + 1}</span>
                {step}
              </li>
            ))}
          </ol>
        </div>
      )}
      {structured.deadline && (
        <div className="flex items-center gap-2 rounded-lg px-3 py-2" style={{ background: 'rgba(245,158,11,0.06)', border: '1px solid rgba(245,158,11,0.2)' }}>
          <span className="text-amber-500" aria-hidden="true">⏰</span>
          <div>
            <p className="text-[10px] font-bold text-amber-700 uppercase tracking-wider">Deadline</p>
            <p className="text-[12px] text-amber-800">{structured.deadline}</p>
          </div>
        </div>
      )}
      {structured.documents && structured.documents.length > 0 && (
        <div>
          <p className="text-[10px] font-bold text-ink-muted uppercase tracking-wider mb-1.5">Documents Required</p>
          <div className="flex flex-wrap gap-1.5">
            {structured.documents.map((doc, i) => (
              <span key={i} className="text-[11px] px-2 py-0.5 rounded-full bg-surface-3 text-ink-muted border border-border">
                <span aria-hidden="true">📄 </span>{doc}
              </span>
            ))}
          </div>
        </div>
      )}
      {structured.source && <SourceBadge source={structured.source} />}
      {structured.humanReview && (
        <div className="rounded-lg px-3 py-2 flex items-start gap-2" style={{ background: 'rgba(16,185,129,0.04)', border: '1px solid rgba(16,185,129,0.15)' }}>
          <span className="text-emerald-500 text-sm mt-0.5" aria-hidden="true">👤</span>
          <div>
            <p className="text-[10px] font-bold text-emerald-700 uppercase tracking-wider">Human Review</p>
            <p className="text-[11px] text-emerald-800">{structured.humanReview}</p>
          </div>
        </div>
      )}
      {structured.actions && structured.actions.length > 0 && (
        <div className="flex flex-wrap gap-2 mt-2">
          {structured.actions.map((action, i) => (
            <a
              key={i}
              href={action.href}
              className="inline-flex items-center gap-1.5 text-[12px] font-semibold text-ai bg-ai-light hover:bg-ai/15 px-3 py-1.5 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai"
            >
              {action.label} →
            </a>
          ))}
        </div>
      )}
    </div>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export default function AssistantPage() {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isStreaming, setIsStreaming] = useState(false);
  const [passport, setPassport] = useState<BusinessPassport>(DEFAULT_PASSPORT);
  const [passportLoaded, setPassportLoaded] = useState(false);
  const [alerts, setAlerts] = useState<ProactiveAlert[]>([]);
  const [aiConfigured, setAiConfigured] = useState<boolean | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  // Auto-scroll to bottom
  const scrollToBottom = useCallback(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages, scrollToBottom]);

  // Load AI config status
  useEffect(() => {
    fetch('/api/ai')
      .then(r => r.json())
      .then(d => setAiConfigured(d.configured))
      .catch(() => setAiConfigured(false));
  }, []);

  // Load Business Passport from Supabase
  useEffect(() => {
    async function loadPassport() {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) { setPassportLoaded(true); return; }

        const { data: business, error: bizErr } = await supabase
          .from('businesses')
          .select(`
            id, legal_name, entity_type, setup_progress,
            founders (full_name, is_director),
            business_activities (description, industry),
            registered_offices (city, state),
            business_passports (completeness_score, msme_number, dpiit_number, gstin),
            compliance_events (title, status, due_date, event_type),
            trademark_applications (id, mark_name, status, trademark_class)
          `)
          .eq('user_id', user.id)
          .limit(1)
          .single();

        if (bizErr && bizErr.code !== 'PGRST116') throw bizErr;

        if (business) {
          const founders = Array.isArray(business.founders) ? business.founders : [];
          const activities = Array.isArray(business.business_activities) ? business.business_activities : [];
          const offices = Array.isArray(business.registered_offices) ? business.registered_offices : [];
          const passportData = Array.isArray(business.business_passports) ? business.business_passports[0] : null;
          const complianceEvents = Array.isArray(business.compliance_events) ? business.compliance_events : [];
          const trademarks = Array.isArray(business.trademark_applications) ? business.trademark_applications : [];

          const overdue = complianceEvents.filter((e: any) => e.status === 'overdue').map((e: any) => e.title);
          const dueSoon = complianceEvents.filter((e: any) => e.status === 'due_soon').map((e: any) => e.title);

          const bp: BusinessPassport = {
            businessName: business.legal_name || 'Your Business',
            entityType: business.entity_type || 'Private Limited Company',
            founders: founders.length,
            directors: founders.filter((f: any) => f.is_director).map((f: any) => f.full_name),
            businessActivity: activities[0]?.description || '',
            address: offices[0] ? `${offices[0].city}, ${offices[0].state}` : '',
            gstStatus: passportData?.gstin ? 'Registered' : 'Not registered',
            msmeStatus: passportData?.msme_number ? 'Registered' : 'Not registered',
            dpiitStatus: passportData?.dpiit_number ? 'Recognized' : 'Not recognized',
            trademarkStatus: trademarks.length > 0 ? `${trademarks.length} application(s)` : 'Not started',
            complianceHealth: passportData?.completeness_score || business.setup_progress || 0,
            pendingItems: [...overdue, ...dueSoon],
            registrations: {
              gst: passportData?.gstin ? 'Registered' : 'Not registered',
              msme: passportData?.msme_number ? 'Registered' : 'Not registered',
              dpiit: passportData?.dpiit_number ? 'Recognized' : 'Not recognized',
            },
          };

          setPassport(bp);

          // Generate proactive alerts from real data
          const generatedAlerts: ProactiveAlert[] = [];
          if (overdue.length > 0) {
            generatedAlerts.push({
              type: 'danger',
              title: `${overdue.length} overdue compliance item${overdue.length > 1 ? 's' : ''}`,
              description: overdue.slice(0, 2).join(', '),
              action: { label: 'View compliance', href: '/compliance' },
              priority: 'high',
            });
          }
          if (dueSoon.length > 0) {
            generatedAlerts.push({
              type: 'warning',
              title: `${dueSoon.length} filing${dueSoon.length > 1 ? 's' : ''} due soon`,
              description: dueSoon.slice(0, 2).join(', '),
              action: { label: 'Review', href: '/compliance' },
              priority: 'medium',
            });
          }
          if (!passportData?.gstin) {
            generatedAlerts.push({
              type: 'info',
              title: 'GST registration may be required',
              description: 'Check your turnover threshold eligibility.',
              action: { label: 'Check GST eligibility', href: '/gst' },
              priority: 'low',
            });
          }
          if (trademarks.length === 0) {
            generatedAlerts.push({
              type: 'info',
              title: 'Trademark not yet filed',
              description: 'Protect your brand name and logo.',
              action: { label: 'Start trademark search', href: '/trademark' },
              priority: 'low',
            });
          }
          setAlerts(generatedAlerts);
        }
      } catch (err) {
        console.error('Passport load error:', err);
        setLoadError('Could not load your business context. AI will work with limited information.');
      } finally {
        setPassportLoaded(true);
      }
    }
    loadPassport();
  }, []);

  const sendMessage = useCallback(async (question: string) => {
    if (!question.trim() || isStreaming) return;

    const userMsg: Message = {
      role: 'user',
      content: question.trim(),
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsStreaming(true);

    // Add streaming placeholder
    const streamingMsg: Message = {
      role: 'ai',
      content: '',
      timestamp: new Date(),
      isStreaming: true,
    };
    setMessages(prev => [...prev, streamingMsg]);

    if (!aiConfigured) {
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          role: 'ai',
          content: '',
          timestamp: new Date(),
          isStreaming: false,
          structured: {
            configurationError: true,
            answer: 'Lexigen AI is not yet configured.',
          },
        };
        return updated;
      });
      setIsStreaming(false);
      return;
    }

    abortRef.current = new AbortController();

    try {
      const res = await fetch('/api/ai/stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question: question.trim(),
          businessContext: passport,
        }),
        signal: abortRef.current.signal,
      });

      if (!res.ok) {
        throw new Error(`AI service error: ${res.status}`);
      }

      const reader = res.body?.getReader();
      if (!reader) throw new Error('No response stream');

      const decoder = new TextDecoder();
      let accumulated = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split('\n');

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.slice(6).trim();
          if (data === '[DONE]') break;
          try {
            const parsed = JSON.parse(data);
            if (parsed.content) {
              accumulated += parsed.content;
              setMessages(prev => {
                const updated = [...prev];
                updated[updated.length - 1] = {
                  ...updated[updated.length - 1],
                  content: accumulated,
                  isStreaming: true,
                };
                return updated;
              });
            }
            if (parsed.error) {
              throw new Error(parsed.error);
            }
          } catch {
            // skip malformed chunks
          }
        }
      }

      // Finalize message
      setMessages(prev => {
        const updated = [...prev];
        updated[updated.length - 1] = {
          ...updated[updated.length - 1],
          content: accumulated,
          isStreaming: false,
        };
        return updated;
      });

    } catch (err: any) {
      if (err.name === 'AbortError') {
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            ...updated[updated.length - 1],
            content: '[Response stopped]',
            isStreaming: false,
          };
          return updated;
        });
      } else {
        setMessages(prev => {
          const updated = [...prev];
          updated[updated.length - 1] = {
            role: 'ai',
            content: '',
            timestamp: new Date(),
            isStreaming: false,
            structured: {
              answer: 'I encountered an error processing your request. Please try again.',
              humanReview: 'If this persists, please contact support.',
            },
          };
          return updated;
        });
      }
    } finally {
      setIsStreaming(false);
      abortRef.current = null;
    }
  }, [aiConfigured, isStreaming, passport]);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    sendMessage(input);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage(input);
    }
  };

  const stopStreaming = () => {
    abortRef.current?.abort();
  };

  return (
    <div className="min-h-screen bg-surface-2 font-sans">
      <AppNav variant="app" activePath="/assistant" />

      <main className="sm:ml-60 pt-14 sm:pt-0 min-h-screen flex flex-col">
        <div className="flex-1 flex flex-col max-w-4xl mx-auto w-full px-4 sm:px-6 py-6">

          {/* Header */}
          <div className="flex items-center gap-3 mb-6">
            <div
              className="w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0"
              style={{ background: 'linear-gradient(135deg, #1a1a2e, #0f3460)', border: '1px solid rgba(99,102,241,0.3)' }}
              aria-hidden="true"
            >
              <svg width={16} height={16} viewBox="0 0 18 18" fill="none">
                <path d="M3 3h5v2H5v8h3v2H3V3z" fill="white" fillOpacity="0.9" />
                <path d="M10 3h5v2h-3v3h3v2h-3v3h3v2h-5V3z" fill="#818cf8" />
              </svg>
            </div>
            <div>
              <h1 className="text-xl font-bold text-ink">Lexigen AI</h1>
              <p className="text-[12px] text-ink-muted">Your AI legal intelligence assistant</p>
            </div>
            {aiConfigured === false && (
              <div className="ml-auto flex items-center gap-2 bg-amber-50 border border-amber-200 rounded-xl px-3 py-1.5" role="alert">
                <span className="text-amber-600 text-sm" aria-hidden="true">⚙️</span>
                <span className="text-[11px] font-semibold text-amber-800">AI not configured</span>
              </div>
            )}
            {aiConfigured === true && (
              <div className="ml-auto flex items-center gap-2 bg-green-50 border border-green-200 rounded-xl px-3 py-1.5">
                <span className="w-2 h-2 bg-green-600 rounded-full" aria-hidden="true" />
                <span className="text-[11px] font-semibold text-green-700">AI Active</span>
              </div>
            )}
          </div>

          {/* Load error */}
          {loadError && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 mb-4 text-[12px] text-amber-800" role="alert">
              {loadError}
            </div>
          )}

          {/* Business context card */}
          {passportLoaded && passport.businessName !== 'Your Business' && (
            <div className="bg-white border border-border rounded-xl p-4 mb-4">
              <div className="flex items-center gap-2 mb-2">
                <span className="text-[10px] font-bold text-ink-subtle uppercase tracking-wider">Business Context</span>
                <span className="text-[10px] bg-green-50 text-green-700 font-semibold px-2 py-0.5 rounded-full border border-green-200">Loaded</span>
              </div>
              <div className="flex flex-wrap gap-2">
                <span className="text-[11px] bg-surface-2 text-ink-muted px-2 py-1 rounded-lg">{passport.businessName}</span>
                <span className="text-[11px] bg-surface-2 text-ink-muted px-2 py-1 rounded-lg">{passport.entityType}</span>
                {passport.gstStatus && <span className="text-[11px] bg-surface-2 text-ink-muted px-2 py-1 rounded-lg">GST: {passport.gstStatus}</span>}
                {passport.trademarkStatus && <span className="text-[11px] bg-surface-2 text-ink-muted px-2 py-1 rounded-lg">TM: {passport.trademarkStatus}</span>}
              </div>
            </div>
          )}

          {/* Proactive Alerts */}
          {alerts.length > 0 && messages.length === 0 && (
            <div className="space-y-2 mb-4">
              {alerts.map((alert, i) => (
                <div
                  key={i}
                  className={`flex items-start justify-between gap-3 p-3 rounded-xl border ${
                    alert.type === 'danger' ? 'bg-red-50 border-red-200' :
                    alert.type === 'warning'? 'bg-amber-50 border-amber-200' : 'bg-indigo-50 border-indigo-200'
                  }`}
                  role="alert"
                >
                  <div className="flex items-start gap-2 min-w-0">
                    <span className="text-sm flex-shrink-0 mt-0.5" aria-hidden="true">
                      {alert.type === 'danger' ? '🔴' : alert.type === 'warning' ? '🟡' : '💡'}
                    </span>
                    <div className="min-w-0">
                      <p className={`text-[12px] font-semibold ${
                        alert.type === 'danger' ? 'text-red-800' :
                        alert.type === 'warning' ? 'text-amber-800' : 'text-indigo-800'
                      }`}>{alert.title}</p>
                      {alert.description && <p className="text-[11px] text-ink-muted mt-0.5 truncate">{alert.description}</p>}
                    </div>
                  </div>
                  <a
                    href={alert.action.href}
                    className={`flex-shrink-0 text-[11px] font-semibold px-2.5 py-1 rounded-lg transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai ${
                      alert.type === 'danger' ? 'bg-red-100 text-red-700 hover:bg-red-200' :
                      alert.type === 'warning'? 'bg-amber-100 text-amber-800 hover:bg-amber-200' : 'bg-indigo-100 text-indigo-700 hover:bg-indigo-200'
                    }`}
                  >
                    {alert.action.label} →
                  </a>
                </div>
              ))}
            </div>
          )}

          {/* Suggested Questions — shown when no messages */}
          {messages.length === 0 && (
            <div className="mb-4">
              <p className="text-[11px] font-semibold text-ink-subtle uppercase tracking-wider mb-3">Suggested questions</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {SUGGESTED_QUESTIONS.map((q, i) => (
                  <button
                    key={i}
                    onClick={() => sendMessage(q)}
                    disabled={isStreaming}
                    className="text-left p-3 bg-white border border-border rounded-xl text-[12px] text-ink-muted hover:text-ink hover:border-ai/30 hover:bg-surface-2 transition-all disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai"
                  >
                    {q}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Messages */}
          <div
            className="flex-1 space-y-4 mb-4 overflow-y-auto"
            role="log"
            aria-label="Conversation"
            aria-live="polite"
          >
            {messages.map((msg, i) => (
              <div
                key={i}
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.role === 'ai' && (
                  <div
                    className="w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 mr-2 mt-1"
                    style={{ background: 'linear-gradient(135deg, #1a1a2e, #0f3460)' }}
                    aria-hidden="true"
                  >
                    <svg width={12} height={12} viewBox="0 0 18 18" fill="none">
                      <path d="M3 3h5v2H5v8h3v2H3V3z" fill="white" fillOpacity="0.9" />
                      <path d="M10 3h5v2h-3v3h3v2h-3v3h3v2h-5V3z" fill="#818cf8" />
                    </svg>
                  </div>
                )}
                <div
                  className={`max-w-[85%] sm:max-w-[75%] rounded-2xl px-4 py-3 ${
                    msg.role === 'user' ?'bg-ai text-white rounded-tr-sm' :'bg-white border border-border rounded-tl-sm'
                  }`}
                >
                  {msg.role === 'user' ? (
                    <p className="text-[13px] leading-relaxed">{msg.content}</p>
                  ) : (
                    <div>
                      {msg.structured ? (
                        <StructuredResponseCard structured={msg.structured} />
                      ) : (
                        <div
                          className="text-[13px] text-ink leading-relaxed"
                          dangerouslySetInnerHTML={{ __html: formatMarkdown(msg.content) }}
                        />
                      )}
                      {msg.isStreaming && (
                        <span className="inline-block w-1.5 h-4 bg-ai/60 rounded-sm animate-pulse ml-1" aria-label="AI is typing" />
                      )}
                    </div>
                  )}
                  <p className="text-[10px] mt-2 opacity-50">
                    {msg.timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </p>
                </div>
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>

          {/* Input */}
          <div className="bg-white border border-border rounded-2xl shadow-sm">
            <form onSubmit={handleSubmit} className="flex items-end gap-3 p-3">
              <textarea
                ref={inputRef}
                value={input}
                onChange={e => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={aiConfigured === false ? 'AI not configured — set OPENROUTER_API_KEY' : 'Ask Lexigen AI anything about your business…'}
                disabled={isStreaming}
                rows={1}
                aria-label="Message to Lexigen AI"
                className="flex-1 resize-none bg-transparent text-[13px] text-ink placeholder-ink-subtle outline-none py-1.5 max-h-32 overflow-y-auto disabled:opacity-50"
                style={{ minHeight: '36px' }}
              />
              {isStreaming ? (
                <button
                  type="button"
                  onClick={stopStreaming}
                  aria-label="Stop AI response"
                  className="flex-shrink-0 p-2.5 bg-red-50 text-red-600 rounded-xl hover:bg-red-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-red-500"
                >
                  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <rect x="6" y="6" width="12" height="12" rx="2" />
                  </svg>
                </button>
              ) : (
                <button
                  type="submit"
                  disabled={!input.trim() || isStreaming}
                  aria-label="Send message"
                  className="flex-shrink-0 p-2.5 bg-ai text-white rounded-xl hover:bg-ai-dark transition-all disabled:opacity-40 disabled:cursor-not-allowed focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ai focus-visible:ring-offset-2"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                </button>
              )}
            </form>
            <div className="px-4 pb-3 flex items-center justify-between">
              <p className="text-[10px] text-ink-subtle">
                Press Enter to send · Shift+Enter for new line
              </p>
              <p className="text-[10px] text-ink-subtle font-semibold">AI PREPARES · HUMANS APPROVE</p>
            </div>
          </div>

          {/* Legal disclaimer */}
          <p className="text-[10px] text-ink-subtle text-center mt-3 leading-relaxed">
            Lexigen AI provides informational guidance only. Not legal advice. Verify with official sources. Consult a qualified professional for legally consequential decisions.
          </p>
        </div>
      </main>
    </div>
  );
}
